import { Helmet } from 'react-helmet-async';
import { Button, Container, Grid, Stack, TextField, Typography, MenuItem, Select, InputLabel, FormControl } from '@mui/material';
import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { createImageCategory, uploadImageAsset, getAdminCategoriesWithAssets, deleteImageAsset, deleteImageCategory } from '../services/Common/imageLibrary.service';
import axios from 'axios';

export default function ImageLibraryPage() {
  const [categories, setCategories] = useState([]);
  const [catForm, setCatForm] = useState({ name: '', slug: '', description: '' });
  const [assetForm, setAssetForm] = useState({ categoryId: '', title: '', tags: '', file: null });
  const [loadingCat, setLoadingCat] = useState(false);
  const [loadingAsset, setLoadingAsset] = useState(false);
  const [browseLoading, setBrowseLoading] = useState(false);
  const [browseFilters, setBrowseFilters] = useState({ categoryId: '', includeInactive: false, includeEmpty: false });
  const [categoryWithAssets, setCategoryWithAssets] = useState([]);

  const loadCategories = async () => {
    try {
      const { data } = await axios.get('/api/v1/image-library/categories', {
        headers: { Authorization: `Bearer ${localStorage.getItem('USER_TOKEN')}` },
      });
      setCategories(data?.data || []);
    } catch (err) {
      console.log(err);
    }
  };

  useEffect(() => {
    loadCategories();
  }, []);

  const loadBrowse = async () => {
    try {
      setBrowseLoading(true);
      const data = await getAdminCategoriesWithAssets({
        categoryId: browseFilters.categoryId || undefined,
        includeInactive: browseFilters.includeInactive,
        includeEmpty: browseFilters.includeEmpty,
      });
      setCategoryWithAssets(data);
    } catch (err) {
      console.log(err);
      toast(err?.response?.data?.message || 'Failed to load assets', { type: 'error' });
    }
    setBrowseLoading(false);
  };

  useEffect(() => {
    loadBrowse();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [browseFilters]);

  const onCreateCategory = async () => {
    const { name, slug, description } = catForm;
    if (!name || !slug) return toast('Name and slug are required', { type: 'error' });
    try {
      setLoadingCat(true);
      await createImageCategory({ name, slug, description });
      toast('Category created', { type: 'success' });
      setCatForm({ name: '', slug: '', description: '' });
      await loadCategories();
    } catch (err) {
      console.log(err);
      toast(err?.response?.data?.message || 'Failed to create category', { type: 'error' });
    }
    setLoadingCat(false);
  };

  const onUploadAsset = async () => {
    const { categoryId, title, tags, file } = assetForm;
    if (!file || !categoryId || !title) return toast('Image, category and title are required', { type: 'error' });
    try {
      setLoadingAsset(true);
      await uploadImageAsset({ file, categoryId, title, tags });
      toast('Image uploaded', { type: 'success' });
      setAssetForm({ categoryId: '', title: '', tags: '', file: null });
    } catch (err) {
      console.log(err);
      toast(err?.response?.data?.message || 'Failed to upload image', { type: 'error' });
    }
    setLoadingAsset(false);
  };

  const onDeleteAsset = async (assetId) => {
    if (!window.confirm('Delete this asset? This will remove the image from S3 and the database.')) return
    try {
      await deleteImageAsset(assetId)
      toast('Asset deleted', { type: 'success' })
      // refresh list
      await loadBrowse()
      await loadCategories()
    } catch (err) {
      console.log(err)
      toast(err?.response?.data?.message || 'Failed to delete asset', { type: 'error' })
    }
  }

  const onDeleteCategory = async (categoryId) => {
    // if category has assets, user must confirm cascade
    if (!window.confirm('Delete this category? Use OK to cascade delete all assets in this category.')) return
    try {
      await deleteImageCategory(categoryId, { cascade: true })
      toast('Category deleted', { type: 'success' })
      await loadBrowse()
      await loadCategories()
    } catch (err) {
      console.log(err)
      toast(err?.response?.data?.message || 'Failed to delete category', { type: 'error' })
    }
  }

  return (
    <>
      <Helmet>
        <title> Image Library | Trooper </title>
      </Helmet>
      <Container>
        <Typography variant="h4" gutterBottom>
          Image Library
        </Typography>
        <Grid container spacing={4}>
          <Grid item xs={12} md={6}>
            <Stack spacing={2}>
              <Typography variant="h6">Create Category</Typography>
              <TextField label="Name" value={catForm.name} onChange={e => setCatForm({ ...catForm, name: e.target.value })} />
              <TextField label="Slug" value={catForm.slug} onChange={e => setCatForm({ ...catForm, slug: e.target.value })} />
              <TextField label="Description" value={catForm.description} onChange={e => setCatForm({ ...catForm, description: e.target.value })} />
              <Button variant="contained" onClick={onCreateCategory} disabled={loadingCat}>Create Category</Button>
            </Stack>
          </Grid>
          <Grid item xs={12} md={6}>
            <Stack spacing={2}>
              <Typography variant="h6">Upload Image</Typography>
              <FormControl>
                <InputLabel id="cat-select">Category</InputLabel>
                <Select labelId="cat-select" label="Category" value={assetForm.categoryId} onChange={e => setAssetForm({ ...assetForm, categoryId: e.target.value })}>
                  {categories.map(c => (
                    <MenuItem key={c._id || c.id} value={c._id || c.id}>{c.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField label="Title" value={assetForm.title} onChange={e => setAssetForm({ ...assetForm, title: e.target.value })} />
              <TextField label="Tags (comma-separated)" value={assetForm.tags} onChange={e => setAssetForm({ ...assetForm, tags: e.target.value })} />
              <Button variant="contained" component="label">
                Choose Image
                <input hidden type="file" accept="image/*" onChange={e => setAssetForm({ ...assetForm, file: e.target.files?.[0] || null })} />
              </Button>
              <Button variant="contained" color="primary" onClick={onUploadAsset} disabled={loadingAsset}>
                Upload Image
              </Button>
            </Stack>
          </Grid>
          <Grid item xs={12}>
            <Stack spacing={2}>
              <Typography variant="h6">Browse (Admin)</Typography>
              <Stack direction="row" spacing={2}>
                <FormControl sx={{ minWidth: 240 }}>
                  <InputLabel id="browse-cat">Category (optional)</InputLabel>
                  <Select labelId="browse-cat" label="Category (optional)" value={browseFilters.categoryId} onChange={e => setBrowseFilters(prev => ({ ...prev, categoryId: e.target.value }))}>
                    <MenuItem value="">All Categories</MenuItem>
                    {categories.map(c => (
                      <MenuItem key={c._id || c.id} value={c._id || c.id}>{c.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl sx={{ minWidth: 200 }}>
                  <InputLabel id="include-inactive">Include Inactive</InputLabel>
                  <Select labelId="include-inactive" label="Include Inactive" value={browseFilters.includeInactive ? 'true' : 'false'} onChange={e => setBrowseFilters(prev => ({ ...prev, includeInactive: e.target.value === 'true' }))}>
                    <MenuItem value={'false'}>No</MenuItem>
                    <MenuItem value={'true'}>Yes</MenuItem>
                  </Select>
                </FormControl>
                <FormControl sx={{ minWidth: 200 }}>
                  <InputLabel id="include-empty">Include Empty</InputLabel>
                  <Select labelId="include-empty" label="Include Empty" value={browseFilters.includeEmpty ? 'true' : 'false'} onChange={e => setBrowseFilters(prev => ({ ...prev, includeEmpty: e.target.value === 'true' }))}>
                    <MenuItem value={'false'}>No</MenuItem>
                    <MenuItem value={'true'}>Yes</MenuItem>
                  </Select>
                </FormControl>
                <Button onClick={loadBrowse} disabled={browseLoading} variant="outlined">Refresh</Button>
              </Stack>

              {/* Render categories with their assets */}
              <Grid container spacing={2}>
                {categoryWithAssets.map((group) => (
                  <Grid key={(group.category && (group.category._id || group.category.id)) || Math.random()} item xs={12}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between">
                      <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>
                        {group?.category?.name || 'Unnamed Category'} ({group?.count ?? 0})
                      </Typography>
                      <Stack direction="row" spacing={1}>
                        <Button size="small" color="error" onClick={() => onDeleteCategory(group?.category?._id)}>
                          Delete Category
                        </Button>
                      </Stack>
                    </Stack>
                    <Grid container spacing={2}>
                        {Array.isArray(group.assets) && group.assets.length > 0 ? (
                        group.assets.map(asset => (
                          <Grid key={asset._id || asset.id} item xs={12} sm={6} md={3} lg={2}>
                            <Stack spacing={1}>
                              <img alt={asset.title || ''} src={asset.url || asset.src || asset.path} style={{ width: '100%', height: 160, objectFit: 'cover', borderRadius: 6 }} />
                              <Typography variant="body2" title={asset.title || ''} noWrap>{asset.title || 'Untitled'}</Typography>
                              {asset.tags && (
                                <Typography variant="caption" color="text.secondary" noWrap>
                                  {Array.isArray(asset.tags) ? asset.tags.join(', ') : String(asset.tags)}
                                </Typography>
                              )}
                              <Stack direction="row" spacing={1}>
                                <Button size="small" color="error" onClick={() => onDeleteAsset(asset._id)}>Delete</Button>
                              </Stack>
                            </Stack>
                          </Grid>
                        ))
                      ) : (
                        <Typography variant="body2" color="text.secondary">No assets</Typography>
                      )}
                    </Grid>
                  </Grid>
                ))}
              </Grid>
            </Stack>
          </Grid>
        </Grid>
      </Container>
    </>
  );
}
